import copyToClipboard from "copy-to-clipboard";
import { PlanrButton } from "modules/common/components/planr/button/Button";
import { getSiteAddress } from "modules/common/services/communication/http";
import { useState } from "react";
import styles from "./CredentialsActions.module.scss";

export interface LoginData {
  login: string;
  password: string;
}

interface CredentialsActionsProps {
  credentials: LoginData;
  /** Чьи данные - заголовок распечатки */
  name?: string;
  onCopied: () => void;
  /** Отправить на почту; нет функции или mailDisabled - кнопка неактивна */
  onMail?: () => Promise<unknown>;
  /** Почему нельзя отправить (нет почты, карточка не сохранена) - подсказкой */
  mailDisabled?: string;
}

const asText = ({ login, password }: LoginData) =>
  `Адрес: ${getSiteAddress()}\nЛогин: ${login}\nПароль: ${password}`;

/**
 * Что сделать с только что сгенерированным паролем: скопировать, отправить на почту,
 * распечатать. Одной строкой: «Копировать» с подписью, почта и печать иконками;
 * недоступное - неактивно с объяснением
 */
export const CredentialsActions = ({ credentials, name, onCopied, onMail, mailDisabled }: CredentialsActionsProps) => {
  const [sending, setSending] = useState(false);
  const canMail = !!onMail && !mailDisabled;

  return (
    <div className={styles.actions}>
      <PlanrButton
        type="greenish"
        size="small"
        icon="general-copy"
        title="Скопировать адрес, логин и пароль"
        onClick={() => {
          copyToClipboard(asText(credentials));
          onCopied();
        }}
      >
        Копировать
      </PlanrButton>
      <PlanrButton
        type="blueish"
        size="small"
        round={true}
        icon="general-mail"
        disabled={!canMail || sending}
        title={canMail ? "Отправить данные для входа на почту" : mailDisabled || "Отправка на почту недоступна"}
        onClick={async () => {
          if (onMail) {
            setSending(true);
            await onMail();
            setSending(false);
          }
        }}
      />
      <PlanrButton
        type="graybtn"
        size="small"
        round={true}
        icon="general-print"
        title="Распечатать данные для входа"
        onClick={() => printCredentials(credentials, name)}
      />
    </div>
  );
};

const escape = (value: string) =>
  value.replace(/[&<>"]/g, (c) => ({ "&": "&amp;", "<": "&lt;", ">": "&gt;", '"': "&quot;" })[c]!);

/** Печать через скрытый фрейм: страница приложения не перерисовывается и не печатается */
export const printCredentials = (credentials: LoginData, name?: string) => {
  const frame = document.createElement("iframe");
  frame.style.position = "fixed";
  frame.style.width = "0";
  frame.style.height = "0";
  frame.style.border = "0";
  document.body.appendChild(frame);

  const doc = frame.contentWindow!.document;
  doc.open();
  doc.write(`<!doctype html><html><head><meta charset="utf-8"><title>Данные для входа</title>
<style>
  body { font-family: Arial, sans-serif; color: #00273d; padding: 40px; }
  h1 { font-size: 20px; margin: 0 0 24px; }
  table { border-collapse: collapse; font-size: 16px; }
  td { padding: 8px 24px 8px 0; }
  td:first-child { color: #6b7d8a; }
  p { margin-top: 32px; font-size: 13px; color: #6b7d8a; }
</style></head><body>
<h1>Данные для входа${name ? `: ${escape(name)}` : ""}</h1>
<table>
  <tr><td>Адрес</td><td>${escape(getSiteAddress())}</td></tr>
  <tr><td>Логин</td><td><b>${escape(credentials.login)}</b></td></tr>
  <tr><td>Пароль</td><td><b>${escape(credentials.password)}</b></td></tr>
</table>
<p>Никому не сообщайте пароль. Сменить его можно в профиле после входа.</p>
</body></html>`);
  doc.close();

  frame.contentWindow!.focus();
  frame.contentWindow!.print();
  // окно печати модальное: после него фрейм больше не нужен
  setTimeout(() => frame.remove(), 1000);
};
